import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem } from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Toast } from 'devextreme-react/toast';
import { DataGrid, Column, Editing, Selection, Scrolling } from 'devextreme-react/data-grid';
import { DataChange } from 'devextreme/common/grids';
import 'devextreme-react/text-area';

import FieldApi from '../../api/FieldApi';
import TableApi from '../../api/TableApi';

import { formatMessage } from 'devextreme/localization';

import ActionType from '../../classes/consts/ActionType';

import LabelTemplate from '../label-template/LabelTemplate';

import TableFormDto from '../../classes/dtos/datasourcemodule/TableFormDto';

import TableReferenceKeyDto from '../../classes/dtos/TableReferenceKeyDto';
import FieldReferenceDto from '../../classes/dtos/datastructuremodule/FieldReferenceDto';
import FieldTypeCell from './FieldTypeCell';

import { JobStatusError } from '../../classes/consts/JobStatus';

import './TableForm.scss';

interface TableFormProps {
    TableAdded(name: string): void;
}

export interface TableFormHandle {
    Open: (databaseId: number, tableId: number | null, originTableId: number | null, originPathId: number | null) => void;
}

const TableForm = forwardRef<TableFormHandle, TableFormProps>(({ TableAdded }, ref) => {

    const position = { of: '#root' };

    const formRef = useRef<FormRef>(null);

    const [databaseId, setDatabaseId] = useState<number>(-1);
    const [tableId, setTableId] = useState<number | null>(null);
    const [originTableId, setOriginTableId] = useState<number | null>(null);
    const [originPathId, setOriginPathId] = useState<number | null>(null);

    const [tableFormDto, setTableFormDto] = useState<TableFormDto>(new TableFormDto(null, []));

    const [fieldReferenceDtos, setFieldReferenceDtos] = useState<FieldReferenceDto[]>([]);
    const [fieldsSelection, setFieldsSelection] = useState<number[]>([]);

    const [isOpen, setIsOpen] = useState(false);

    const [action, setAction] = useState<string>("");

    const [saveInProgress, setSaveInProgress] = React.useState(false);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success' | 'warning',
        message: string,
        withRedirect: boolean
    });

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });
    }, [toastConfig]);

    useImperativeHandle(ref, () => ({
        Open(databaseId: number, tableId: number | null, originTableId: number | null, originPathId: number | null) {

            if (tableId === null) {
                setAction(ActionType.Create);
            } else {
                setAction(ActionType.Update);
            }

            setDatabaseId(databaseId);
            setTableId(tableId);
            setOriginTableId(originTableId);
            setOriginPathId(originPathId);

            setTableFormDto(new TableFormDto(null, []));
            setFieldReferenceDtos([]);
            setFieldsSelection([]);

            setIsOpen(true);
        }
    }));

    const tableNameEditorOptions = {
        valueChangeEvent: 'keyup',
        required: true
    };

    const validationRules: {
        tableName: ValidationRule[],
    } = {
        tableName: [
            { type: 'required', message: 'Table name is required.' },
        ]
    };

    const closeDialog = () => {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        };
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        if (tableFormDto.Name === null) {
            return;
        }

        if (tableFormDto.Fields.length === 0) {
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: 'error',
                message: formatMessage('TableForm_NoFields'),
                withRedirect: false
            });
            return;
        }

        let selectedFields = tableFormDto.Fields.filter((e) => fieldsSelection.find((id) => id === e.Id));

        if (selectedFields.length === 0) {
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: 'error',
                message: formatMessage('TableForm_NoFieldsSelected'),
                withRedirect: false
            });
            return;
        }

        if (selectedFields.filter((e) => e.IsPrimaryKey).length === 0) {
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: 'error',
                message: formatMessage('TableForm_NoPrimaryKeySelected'),
                withRedirect: false
            });
            return;
        }

        setSaveInProgress(true);

        let result = await TableApi.CreateTableAsync(databaseId, new TableReferenceKeyDto("", tableFormDto.Name), selectedFields);

        if (result.IsFailed()) {
            alert("Error creating table: " + result.ErrorMessage);
            setSaveInProgress(false);
            return;
        }

        if (result.Result.Status === JobStatusError) {
            alert("Error creating table: " + result.Result.ErrorMessage);
            setSaveInProgress(false);
            return;
        }

        TableAdded(tableFormDto.Name);
        setIsOpen(false);
        setSaveInProgress(false);
    };

    const copy = async () => {
        if (originTableId !== null || originPathId !== null) {
            let getFieldsResult = await FieldApi.GetComputedFieldsAsync(originTableId, originPathId);
            if (getFieldsResult.IsFailed()) {
                alert("Error loading fields from source: " + getFieldsResult.ErrorMessage);
            }
            else {
                let id = -1;
                let fields = getFieldsResult.Result.Fields.map((f) => {
                    return new FieldReferenceDto(id--, f.Name, f.Type.Type, f.Type.IsMultiple, f.Type.IsBusinessRequired || !f.Type.IsNullable, f.Type.IsPrimaryKey, f.Type.TypeLabel);
                });
                setFieldReferenceDtos(fields);
                setTableFormDto(new TableFormDto(tableFormDto.Name, fields));
                setFieldsSelection(fields.map(f => f.Id));
            }
        }
    };

    const close = () => {
        setIsOpen(false);
    };

    const copyButtonOptions = {
        text: 'Copy from source',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: copy,
        enabled: originTableId !== null || originPathId !== null
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: close
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    function getTitle() {
        switch (action) {
            case ActionType.Create: return formatMessage("TableForm_TitleCreate");
            case ActionType.Update: return formatMessage("TableForm_TitleUpdate");
            default: return "???";
        }
    }

    const handleSelectionChanged = useCallback((e: any) => {
        setFieldsSelection(e.selectedRowKeys);
    }, []);

    const handleOnChangesChange = (changes: DataChange[]): void => {
        changes.forEach((e: DataChange) => {
            let row = fieldReferenceDtos.find((x) => x.Id === e.key) ?? null;
            if (row !== null) {
                if (e.data.IsPrimaryKey !== undefined) {
                    row.IsPrimaryKey = e.data.IsPrimaryKey;
                }
                if (e.data.IsRequired !== undefined) {
                    row.IsRequired = e.data.IsRequired;
                }
            }
        });
    }

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={closeDialog}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={getTitle()}
                container=".dx-viewport"
                width={1000}
                height="auto"
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        ref={formRef}
                        formData={tableFormDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="TableDtoData"
                        colCount={2}
                        disabled={saveInProgress}
                    >
                        <GroupItem colSpan={2}>
                            <ButtonItem buttonOptions={copyButtonOptions} />
                        </GroupItem>
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="Name" editorOptions={tableNameEditorOptions} validationRules={validationRules.tableName}>
                                <Label text={"Table name"} render={LabelTemplate('rename')} />
                            </SimpleItem>
                        </GroupItem>
                        <GroupItem colSpan={2}>
                            <DataGrid
                                dataSource={fieldReferenceDtos}
                                height="500px"
                                keyExpr="Id"
                                className={"datagrid-without-header"}
                                selectedRowKeys={fieldsSelection}
                                onSelectionChanged={handleSelectionChanged}>
                                <Editing
                                    mode="batch"
                                    allowUpdating={true}
                                    allowAdding={false}
                                    allowDeleting={false}
                                    refreshMode="repaint"
                                    onChangesChange={handleOnChangesChange}
                                />
                                <Scrolling mode="standard" />
                                <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" />
                                <Column caption="KEY" dataField="IsPrimaryKey" dataType="boolean" allowEditing={true} width='15%' />
                                <Column caption="NAME" dataField="Name" dataType="string" allowEditing={false} width='50%' />
                                <Column caption="FIELD TYPE" dataField="Type" dataType="string" allowEditing={false} width='20%'
                                    cellRender={FieldTypeCell}
                                    calculateDisplayValue={(rowData: FieldReferenceDto) => rowData.TypeLabel}
                                    calculateSortValue={(rowData: FieldReferenceDto) => rowData.TypeLabel}
                                    filterOperations={["contains"]}
                                />
                                <Column caption="MANDATORY" dataField="IsRequired" dataType="boolean" allowEditing={true} width='15%' />
                            </DataGrid>
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={saveInProgress}
            />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={toastConfig.type === 'success' ? 1000 : 3000}
                defaultPosition='center'
            />
        </React.Fragment>
    );
});

export default TableForm;